/**
 * ShippingPanel — full shipping/logistics section on an admin ClaimDetail page.
 *
 * Displays courier + tracking + label + shipment history.
 * If the user has admin | support | logistics role, exposes:
 *   - Generate outbound label (customer → workshop)
 *   - Generate return label (workshop → customer)
 *   - Copy pickup deep-link (Royal Mail Parcel Collect for the customer)
 *   - Download label PDF
 *
 * All actions hit /api/shipping/* endpoints served by routes_shipping.py.
 */
import React, { useState } from "react";
import { Truck, Package, ExternalLink, Loader2, Copy, Download, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const SERVICES = [
  { code: "tracked24", label: "Royal Mail Tracked 24 (next day)" },
  { code: "tracked48", label: "Royal Mail Tracked 48 (2-3 day)" },
  { code: "special_delivery", label: "Special Delivery (guaranteed)" },
];

const STATUS_COLORS = {
  label_created: "bg-primary/15 text-primary",
  in_transit: "bg-secondary/15 text-secondary",
  delivered: "bg-emerald-100 text-emerald-800",
  problem: "bg-destructive/10 text-destructive",
};

function Shipment({ s, canManage }) {
  const label = s.direction === "outbound" ? "Customer → Workshop" : "Workshop → Customer";
  const icon = s.direction === "outbound" ? Package : Truck;
  const Icon = icon;
  return (
    <div className="rounded-lg border border-border/60 p-3 space-y-2" data-testid={`shipment-${s.direction}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Icon className="w-3.5 h-3.5" />
          <span>{label}</span>
        </div>
        <span className={`text-[10px] overline px-2 py-0.5 rounded-full ${STATUS_COLORS[s.status] || "bg-muted text-muted-foreground"}`}>
          {(s.status || "created").replace(/_/g, " ")}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <span className="font-mono">{s.tracking_number || "—"}</span>
        {s.cost_gbp != null && <span className="text-muted-foreground">£{Number(s.cost_gbp).toFixed(2)}</span>}
        {s.service && <span className="text-muted-foreground">{s.service.replace(/_/g, " ")}</span>}
      </div>
      <div className="flex flex-wrap gap-2">
        {s.tracking_url && (
          <a
            href={s.tracking_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-primary underline"
            data-testid={`track-${s.direction}`}
          >
            Track parcel <ExternalLink className="w-3 h-3" />
          </a>
        )}
        {canManage && s.parcel_id && (
          <button
            onClick={async () => {
              try {
                const r = await api.get(`/shipping/label/${s.parcel_id}`, { responseType: "blob" });
                const url = URL.createObjectURL(r.data);
                const a = document.createElement("a");
                a.href = url;
                a.download = `label-${s.tracking_number || s.parcel_id}.pdf`;
                a.click();
                URL.revokeObjectURL(url);
              } catch (e) {
                toast.error(formatApiError(e.response?.data?.detail) || "Label download failed");
              }
            }}
            className="inline-flex items-center gap-1 text-xs text-primary underline"
            data-testid={`download-label-${s.direction}`}
          >
            Download label <Download className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}

export default function ShippingPanel({ claim, canManage, onRefresh }) {
  const [service, setService] = useState("tracked24");
  const [weight, setWeight] = useState("0.5");
  const [creatingOutbound, setCreatingOutbound] = useState(false);
  const [creatingReturn, setCreatingReturn] = useState(false);
  const [pickupInfo, setPickupInfo] = useState(null);
  const [loadingPickup, setLoadingPickup] = useState(false);
  const [addr, setAddr] = useState({
    name: claim.user_name || "",
    address: "",
    house_number: "",
    city: "",
    postcode: "",
    phone: claim.user_phone || "",
    email: claim.user_email || "",
  });

  const outbound = (claim.shipments || []).find((s) => s.direction === "outbound");
  const rtn = (claim.shipments || []).find((s) => s.direction === "return");

  const createLabel = async (direction) => {
    const setter = direction === "outbound" ? setCreatingOutbound : setCreatingReturn;
    // Basic validation — Sendcloud requires postal_code + city + house_number
    if (!addr.postcode?.trim() || !addr.city?.trim() || !addr.address?.trim()) {
      toast.error("Please enter customer address (line 1, city and postcode)");
      return;
    }
    setter(true);
    try {
      const payload = {
        service,
        weight_kg: parseFloat(weight) || 0.5,
        customer_name: addr.name || "Customer",
        customer_address: addr.address,
        customer_house_number: addr.house_number || "1",
        customer_city: addr.city,
        customer_postcode: addr.postcode,
        customer_country: "GB",
        customer_phone: addr.phone || "",
        customer_email: addr.email || "",
      };
      const res = await api.post(`/shipping/claims/${claim.id}/${direction}-label`, payload);
      toast.success(`${direction === "outbound" ? "Outbound" : "Return"} label created · ${res.data.tracking_number}`);
      onRefresh?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || `${direction} label failed`);
    } finally {
      setter(false);
    }
  };

  const fetchPickupLink = async () => {
    setLoadingPickup(true);
    try {
      const res = await api.get(`/shipping/claims/${claim.id}/pickup-link`);
      setPickupInfo(res.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not fetch pickup link");
    } finally {
      setLoadingPickup(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-6 space-y-4" data-testid="shipping-panel">
      <div className="flex items-center justify-between">
        <div className="overline text-secondary flex items-center gap-2">
          <Truck className="w-3 h-3" /> Shipping
        </div>
        {onRefresh && (
          <button onClick={onRefresh} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="shipping-refresh">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        )}
      </div>

      {/* Existing shipments */}
      {(outbound || rtn) ? (
        <div className="space-y-2">
          {outbound && <Shipment s={outbound} canManage={canManage} />}
          {rtn && <Shipment s={rtn} canManage={canManage} />}
        </div>
      ) : (
        <div className="text-xs text-muted-foreground italic">No shipments yet.</div>
      )}

      {/* Admin controls to create labels */}
      {canManage && (
        <div className="pt-2 border-t border-border/50 space-y-3">
          <div className="text-xs text-muted-foreground">Create label</div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">Service</div>
              <Select value={service} onValueChange={setService}>
                <SelectTrigger data-testid="shipping-service"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SERVICES.map((s) => <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">Weight (kg)</div>
              <Input
                type="number"
                min="0.05"
                max="20"
                step="0.05"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                data-testid="shipping-weight"
              />
            </div>
          </div>

          {/* Customer address (required by Royal Mail) */}
          <div className="space-y-2">
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Customer address</div>
            <Input placeholder="Full name" value={addr.name}
                   onChange={(e) => setAddr({ ...addr, name: e.target.value })}
                   data-testid="ship-customer-name" />
            <div className="grid grid-cols-3 gap-2">
              <Input placeholder="House no." value={addr.house_number}
                     onChange={(e) => setAddr({ ...addr, house_number: e.target.value })}
                     data-testid="ship-customer-house" />
              <Input className="col-span-2" placeholder="Street / Line 1" value={addr.address}
                     onChange={(e) => setAddr({ ...addr, address: e.target.value })}
                     data-testid="ship-customer-address" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="City" value={addr.city}
                     onChange={(e) => setAddr({ ...addr, city: e.target.value })}
                     data-testid="ship-customer-city" />
              <Input placeholder="Postcode" value={addr.postcode}
                     onChange={(e) => setAddr({ ...addr, postcode: e.target.value.toUpperCase() })}
                     data-testid="ship-customer-postcode" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Phone" value={addr.phone}
                     onChange={(e) => setAddr({ ...addr, phone: e.target.value })}
                     data-testid="ship-customer-phone" />
              <Input placeholder="Email" value={addr.email}
                     onChange={(e) => setAddr({ ...addr, email: e.target.value })}
                     data-testid="ship-customer-email" />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {!outbound && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => createLabel("outbound")}
                disabled={creatingOutbound}
                data-testid="create-outbound-label"
              >
                {creatingOutbound ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Package className="w-3 h-3 mr-1" />}
                Outbound label
              </Button>
            )}
            {!rtn && (
              <Button
                size="sm"
                onClick={() => createLabel("return")}
                disabled={creatingReturn}
                data-testid="create-return-label"
              >
                {creatingReturn ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Truck className="w-3 h-3 mr-1" />}
                Return label
              </Button>
            )}
            {outbound && (
              <Button
                size="sm"
                variant="outline"
                onClick={fetchPickupLink}
                disabled={loadingPickup}
                data-testid="fetch-pickup-link"
              >
                {loadingPickup ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Copy className="w-3 h-3 mr-1" />}
                Get pickup link
              </Button>
            )}
          </div>
          {pickupInfo && (
            <div className="text-xs bg-muted/40 rounded-lg p-3 space-y-2" data-testid="pickup-info">
              <div className="text-muted-foreground">Send this link to the customer — they book their own home collection with Royal Mail (30p per parcel, reimbursed via wallet):</div>
              <div className="flex items-center gap-2">
                <code className="flex-1 truncate text-[10px]">{pickupInfo.pickup_url}</code>
                <button
                  onClick={() => { navigator.clipboard.writeText(pickupInfo.pickup_url); toast.success("Pickup link copied"); }}
                  className="text-primary hover:text-primary/80"
                  data-testid="copy-pickup-link"
                >
                  <Copy className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
